import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Users, Gift, Copy, CheckCircle, Share, Trophy, Coins, UserPlus, Clock, Link } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralPage = () => {
  const navigate = useNavigate();
  const [referralData, setReferralData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Get user identity from localStorage
  const irisId = localStorage.getItem('ginsgood_iris_id');
  const irisName = localStorage.getItem('ginsgood_iris_name');
  const irisType = localStorage.getItem('ginsgood_iris_type') || 'seller';

  const fetchReferralData = useCallback(async () => {
    try {
      const [myCodeRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/referral/my-code/${irisType}/${irisId}`),
        axios.get(`${API}/referral/leaderboard?limit=5`)
      ]);

      setReferralData(myCodeRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, [irisType, irisId]);

  useEffect(() => {
    if (!irisId) {
      toast.error('Please complete iris authentication first');
      navigate('/iris-auth');
      return;
    }
    fetchReferralData();
  }, [irisId, navigate, fetchReferralData]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = async () => {
    const code = referralData?.referral_code?.code;
    const shareText = `Join GinsGood - the sustainability certification platform! Use my referral code ${code} to get started. 🌿`;
    const shareUrl = `${window.location.origin}/iris-auth?ref=${code}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join GinsGood',
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        copyToClipboard(`${shareText}\n${shareUrl}`);
      }
    } else {
      copyToClipboard(`${shareText}\n${shareUrl}`);
    }
  };

  const getRewardAmount = () => {
    const typeRewards = {
      company: 2000,
      seller: 500,
      artisan: 500,
      customer: 200
    };
    return typeRewards[irisType] || 200;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="animate-pulse">Loading referral data...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const stats = referralData?.stats || { total_referrals: 0, completed: 0, pending: 0, total_earned: 0 };
  const code = referralData?.referral_code?.code || 'Loading...';

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge variant="outline" className="mb-4">
              <Gift weight="duotone" className="w-4 h-4 mr-2" />
              Refer & Earn
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Invite Friends, Earn GSG
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Share the sustainability movement and earn GinsGoodCoin for every friend who joins!
            </p>
          </div>

          {/* Reward Banner */}
          <Card className="p-8 mb-8 bg-gradient-to-br from-[#4A6C4C] to-[#2C3E2D] text-white text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Coins weight="fill" className="w-10 h-10 text-yellow-400" />
              <span className="text-5xl font-bold">{getRewardAmount()}</span>
              <span className="text-2xl">GSG</span>
            </div>
            <p className="text-lg opacity-90">for each successful referral</p>
            <p className="text-sm opacity-70 mt-2">
              {irisType === 'company' ? 'Earn when referred companies certify their first product' : 
               'Earn when your friends complete their first certification'}
            </p>
          </Card>

          {/* Your Referral Code */}
          <Card className="p-6 mb-8" data-testid="referral-code-card">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Link weight="duotone" className="w-5 h-5" />
              Your Referral Code
            </h3>
            
            <div className="bg-muted rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center gap-4">
                <code className="text-3xl font-bold tracking-wider text-primary" data-testid="referral-code">
                  {code}
                </code>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => copyToClipboard(code)}
                  data-testid="copy-code-btn"
                >
                  {copied ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={shareReferral} 
                className="flex-1 gap-2 h-12"
                data-testid="share-btn"
              >
                <Share weight="bold" className="w-5 h-5" />
                Share Your Code
              </Button>
              <Button 
                variant="outline" 
                onClick={() => copyToClipboard(`${window.location.origin}/iris-auth?ref=${code}`)}
                className="flex-1 gap-2 h-12"
                data-testid="copy-link-btn"
              >
                <Copy className="w-5 h-5" />
                Copy Invite Link
              </Button>
            </div>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 text-center" data-testid="stat-total">
              <Users weight="duotone" className="w-8 h-8 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold">{stats.total_referrals}</p>
              <p className="text-sm text-muted-foreground">Total Referrals</p>
            </Card>
            <Card className="p-4 text-center" data-testid="stat-completed">
              <CheckCircle weight="duotone" className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">{stats.completed}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </Card>
            <Card className="p-4 text-center" data-testid="stat-pending">
              <Clock weight="duotone" className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </Card>
            <Card className="p-4 text-center" data-testid="stat-earned">
              <Coins weight="duotone" className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p className="text-2xl font-bold">{stats.total_earned?.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">GSG Earned</p>
            </Card>
          </div>

          {/* How It Works */}
          <Card className="p-6 mb-8">
            <h3 className="text-lg font-medium mb-6 flex items-center gap-2">
              <UserPlus weight="duotone" className="w-5 h-5" />
              How It Works
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">1</span>
                </div>
                <h4 className="font-medium mb-1">Share Your Code</h4>
                <p className="text-sm text-muted-foreground">
                  Send your unique referral code to friends, businesses, or artisans
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">2</span>
                </div>
                <h4 className="font-medium mb-1">They Sign Up</h4>
                <p className="text-sm text-muted-foreground">
                  Your friend registers on GinsGood using your referral code
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">3</span>
                </div>
                <h4 className="font-medium mb-1">Earn GSG</h4>
                <p className="text-sm text-muted-foreground">
                  Get {getRewardAmount()} GSG when they complete their first action
                </p>
              </div>
            </div>
          </Card>

          {/* Recent Referrals */}
          {referralData?.recent_referrals?.length > 0 && (
            <Card className="p-6 mb-8">
              <h3 className="text-lg font-medium mb-4">Recent Referrals</h3>
              <div className="space-y-3">
                {referralData.recent_referrals.map((ref, idx) => (
                  <div 
                    key={ref.id || idx}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    data-testid={`referral-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        ref.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                      }`}>
                        {ref.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{ref.referred_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{ref.referred_type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={ref.status === 'completed' ? 'default' : 'secondary'}>
                        {ref.status === 'completed' ? `+${ref.reward_amount} GSG` : 'Pending'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Top Referrers */}
          {leaderboard.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Trophy weight="duotone" className="w-5 h-5 text-yellow-500" />
                Top Referrers
              </h3>
              <div className="space-y-3">
                {leaderboard.map((entry, idx) => (
                  <div 
                    key={entry.owner_id || entry.id || `leaderboard-${idx}`}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      entry.owner_id === irisId ? 'bg-primary/10 border border-primary' : 'bg-muted/50'
                    }`}
                    data-testid={`leaderboard-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-gray-100 text-gray-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-muted'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium">{entry.owner_name}</p>
                        <p className="text-xs text-muted-foreground">{entry.referral_count} referrals</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 font-bold">
                      <Coins className="w-4 h-4 text-yellow-500" />
                      {entry.total_earned?.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* CTA */}
          <div className="mt-8 text-center">
            <Button onClick={() => navigate('/wallet')} variant="outline" className="gap-2">
              <Coins className="w-4 h-4" />
              View Your Wallet
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReferralPage;
